/**
 * Migration Path Planner Utilities
 */

import {
  MigrationPlan,
  MigrationWave,
  MigrationWaveStatus,
  MigrationPhase,
  ExtendedArchiMateDiagram,
  Currency,
} from './extended-types';
import { ArchiMateElement } from './types';
import { uuidv7 } from 'uuidv7';

export function createMigrationPlan(
  name: string,
  description: string,
  currentStateDiagramId: string,
  targetStateDiagramId: string
): MigrationPlan {
  return {
    id: uuidv7(),
    name,
    description,
    currentStateDiagramId,
    targetStateDiagramId,
    waves: [],
    timeline: {
      start: new Date().toISOString().split('T')[0],
      end: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      milestones: [],
    },
  };
}

export function createMigrationWave(
  name: string,
  description: string,
  elements: string[]
): MigrationWave {
  return {
    id: uuidv7(),
    name,
    description,
    status: 'planned',
    elements,
    dependencies: [],
    risks: [],
    successCriteria: [],
  };
}

export function addWaveToPlan(plan: MigrationPlan, wave: MigrationWave): MigrationPlan {
  return {
    ...plan,
    waves: [...plan.waves, wave],
  };
}

export function updateWaveStatus(
  plan: MigrationPlan,
  waveId: string,
  status: MigrationWaveStatus
): MigrationPlan {
  return {
    ...plan,
    waves: plan.waves.map((wave) =>
      wave.id === waveId ? { ...wave, status } : wave
    ),
  };
}

export function detectChangedElements(
  currentDiagram: ExtendedArchiMateDiagram,
  targetDiagram: ExtendedArchiMateDiagram
): {
  added: ArchiMateElement[];
  removed: ArchiMateElement[];
  modified: Array<{ current: ArchiMateElement; target: ArchiMateElement }>;
  unchanged: ArchiMateElement[];
} {
  const added: ArchiMateElement[] = [];
  const removed: ArchiMateElement[] = [];
  const modified: Array<{ current: ArchiMateElement; target: ArchiMateElement }> = [];
  const unchanged: ArchiMateElement[] = [];

  // Find elements by name (assuming name is stable identifier)
  const currentByName = new Map(currentDiagram.elements.map((e) => [e.name, e]));
  const targetByName = new Map(targetDiagram.elements.map((e) => [e.name, e]));

  // Find added and modified
  targetDiagram.elements.forEach((targetEl) => {
    const currentEl = currentByName.get(targetEl.name);
    if (!currentEl) {
      added.push(targetEl);
    } else if (currentEl.type !== targetEl.type || currentEl.layer !== targetEl.layer) {
      modified.push({ current: currentEl, target: targetEl });
    } else {
      unchanged.push(currentEl);
    }
  });

  // Find removed
  currentDiagram.elements.forEach((currentEl) => {
    if (!targetByName.has(currentEl.name)) {
      removed.push(currentEl);
    }
  });

  return { added, removed, modified, unchanged };
}

export function suggestMigrationWaves(
  currentDiagram: ExtendedArchiMateDiagram,
  targetDiagram: ExtendedArchiMateDiagram
): MigrationWave[] {
  const changes = detectChangedElements(currentDiagram, targetDiagram);
  const waves: MigrationWave[] = [];

  // Wave 1: Infrastructure changes (technology/physical layers)
  const infraChanges = [
    ...changes.added.filter((e) => e.layer === 'technology' || e.layer === 'physical'),
    ...changes.modified
      .filter(({ target }) => target.layer === 'technology' || target.layer === 'physical')
      .map(({ target }) => target),
  ];

  if (infraChanges.length > 0) {
    waves.push({
      id: uuidv7(),
      name: 'Wave 1: Infrastructure Setup',
      description: 'Provision and configure infrastructure components',
      status: 'planned',
      elements: infraChanges.map((e) => e.id),
      dependencies: [],
      risks: ['Infrastructure provisioning delays', 'Configuration errors'],
      successCriteria: ['All infrastructure components provisioned', 'Connectivity tests pass'],
    });
  }

  // Wave 2: Application changes
  const appChanges = [
    ...changes.added.filter((e) => e.layer === 'application'),
    ...changes.modified
      .filter(({ target }) => target.layer === 'application')
      .map(({ target }) => target),
  ];

  if (appChanges.length > 0) {
    waves.push({
      id: uuidv7(),
      name: 'Wave 2: Application Migration',
      description: 'Deploy and configure application components',
      status: 'planned',
      elements: appChanges.map((e) => e.id),
      dependencies: waves.length > 0 ? [waves[0].id] : [],
      risks: ['Application downtime', 'Data migration issues'],
      successCriteria: ['All applications deployed', 'Integration tests pass'],
    });
  }

  // Wave 3: Business process changes
  const businessChanges = [
    ...changes.added.filter((e) => e.layer === 'business'),
    ...changes.modified
      .filter(({ target }) => target.layer === 'business')
      .map(({ target }) => target),
  ];

  if (businessChanges.length > 0) {
    waves.push({
      id: uuidv7(),
      name: 'Wave 3: Business Process Updates',
      description: 'Update business processes and workflows',
      status: 'planned',
      elements: businessChanges.map((e) => e.id),
      dependencies: waves.length > 0 ? [waves[waves.length - 1].id] : [],
      risks: ['User adoption challenges', 'Training gaps'],
      successCriteria: ['Training completed', 'User acceptance tests pass'],
    });
  }

  // Wave 4: Decommission old components
  if (changes.removed.length > 0) {
    waves.push({
      id: uuidv7(),
      name: 'Wave 4: Decommissioning',
      description: 'Remove deprecated components',
      status: 'planned',
      elements: changes.removed.map((e) => e.id),
      dependencies: waves.length > 0 ? waves.map((w) => w.id) : [],
      risks: ['Hidden dependencies', 'Data loss'],
      successCriteria: ['All dependencies verified', 'Data archived/migrated'],
    });
  }

  return waves;
}

export function calculateWaveProgress(wave: MigrationWave): number {
  switch (wave.status) {
    case 'not-started':
      return 0;
    case 'planned':
      return 25;
    case 'in-progress':
      return 50;
    case 'blocked':
      return 50;
    case 'completed':
      return 100;
  }
}

export function calculatePlanProgress(plan: MigrationPlan): number {
  if (plan.waves.length === 0) return 0;

  const totalProgress = plan.waves.reduce(
    (sum, wave) => sum + calculateWaveProgress(wave),
    0
  );

  return totalProgress / plan.waves.length;
}

export function getNextWave(plan: MigrationPlan): MigrationWave | null {
  // Find first non-completed wave whose dependencies are all completed
  for (const wave of plan.waves) {
    if (wave.status === 'completed') continue;

    const dependenciesMet = wave.dependencies.every((depId) => {
      const depWave = plan.waves.find((w) => w.id === depId);
      return depWave?.status === 'completed';
    });

    if (dependenciesMet) {
      return wave;
    }
  }

  return null;
}

export function generateMigrationTimeline(plan: MigrationPlan): string {
  let timeline = `# Migration Timeline: ${plan.name}\n\n`;
  timeline += `${plan.description}\n\n`;

  timeline += `**Start Date:** ${plan.timeline.start}\n`;
  timeline += `**End Date:** ${plan.timeline.end}\n`;
  timeline += `**Progress:** ${calculatePlanProgress(plan).toFixed(0)}%\n\n`;

  timeline += `## Waves\n\n`;
  plan.waves.forEach((wave, index) => {
    const progress = calculateWaveProgress(wave);
    const statusEmoji = {
      'not-started': '⚪',
      planned: '🔵',
      'in-progress': '🟡',
      blocked: '🔴',
      completed: '🟢',
    }[wave.status];

    timeline += `### ${statusEmoji} ${wave.name} (${progress}%)\n\n`;
    timeline += `${wave.description}\n\n`;

    if (wave.startDate && wave.endDate) {
      timeline += `**Timeline:** ${wave.startDate} → ${wave.endDate}\n\n`;
    }

    timeline += `**Status:** ${wave.status}\n`;
    timeline += `**Elements:** ${wave.elements.length}\n\n`;

    if (wave.dependencies.length > 0) {
      timeline += `**Dependencies:**\n`;
      wave.dependencies.forEach((depId) => {
        const depWave = plan.waves.find((w) => w.id === depId);
        if (depWave) {
          timeline += `- ${depWave.name}\n`;
        }
      });
      timeline += `\n`;
    }

    if (wave.risks.length > 0) {
      timeline += `**Risks:**\n`;
      wave.risks.forEach((risk) => {
        timeline += `- ${risk}\n`;
      });
      timeline += `\n`;
    }

    if (wave.successCriteria.length > 0) {
      timeline += `**Success Criteria:**\n`;
      wave.successCriteria.forEach((criterion) => {
        timeline += `- ${criterion}\n`;
      });
      timeline += `\n`;
    }
  });

  if (plan.timeline.milestones.length > 0) {
    timeline += `## Milestones\n\n`;
    plan.timeline.milestones.forEach((milestone) => {
      timeline += `- **${milestone.date}:** ${milestone.name}\n`;
      if (milestone.description) {
        timeline += `  ${milestone.description}\n`;
      }
    });
  }

  return timeline;
}

export function validateMigrationPlan(plan: MigrationPlan): string[] {
  const errors: string[] = [];

  // Check for circular dependencies
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  function hasCycle(waveId: string): boolean {
    if (recursionStack.has(waveId)) return true;
    if (visited.has(waveId)) return false;

    visited.add(waveId);
    recursionStack.add(waveId);

    const wave = plan.waves.find((w) => w.id === waveId);
    if (wave) {
      for (const depId of wave.dependencies) {
        if (hasCycle(depId)) return true;
      }
    }

    recursionStack.delete(waveId);
    return false;
  }

  plan.waves.forEach((wave) => {
    if (hasCycle(wave.id)) {
      errors.push(`Circular dependency detected in wave: ${wave.name}`);
    }
  });

  // Check for invalid dependencies
  plan.waves.forEach((wave) => {
    wave.dependencies.forEach((depId) => {
      const depWave = plan.waves.find((w) => w.id === depId);
      if (!depWave) {
        errors.push(`Invalid dependency in wave "${wave.name}": ${depId}`);
      }
    });
  });

  // Check for waves with no elements
  plan.waves.forEach((wave) => {
    if (wave.elements.length === 0) {
      errors.push(`Wave "${wave.name}" has no elements`);
    }
  });

  return errors;
}

export function exportMigrationPlanToJSON(plan: MigrationPlan): string {
  return JSON.stringify(plan, null, 2);
}

export function importMigrationPlanFromJSON(json: string): MigrationPlan {
  return JSON.parse(json);
}

// Aliases for backward compatibility
export const addMigrationWave = addWaveToPlan;
export const exportMigrationPlan = exportMigrationPlanToJSON;
export function calculateMigrationProgress(plan: MigrationPlan): {
  total: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  percentage: number;
} {
  const total = plan.waves.length;
  const completed = plan.waves.filter(w => w.status === 'completed').length;
  const inProgress = plan.waves.filter(w => w.status === 'in-progress').length;
  const notStarted = plan.waves.filter(w => w.status === 'not-started').length;
  const percentage = calculatePlanProgress(plan);

  return { total, completed, inProgress, notStarted, percentage };
}
